import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './App.css';

// Map logo assets to categories
const CATEGORY_LOGOS = {
    kantor_polisi: '/image/polisi.png',
    pos_pam: '/image/shield.png',
    rumah_sakit: '/image/rumahsakit.png',
    spbu: '/image/spbu.png',
    wisata: '/image/wisata.png'
};

const CATEGORIES = [
    { id: 'kantor_polisi', label: 'Kantor Polisi', emoji: '👮', color: '#3B82F6' },
    { id: 'pos_pam', label: 'Pos Pam & Yan', emoji: '🛡️', color: '#EF4444' },
    { id: 'rumah_sakit', label: 'Rumah Sakit', emoji: '🏥', color: '#10B981' },
    { id: 'spbu', label: 'SPBU Strategis', emoji: '⛽', color: '#F59E0B' },
    { id: 'wisata', label: 'Wisata', emoji: '🎡', color: '#8B5CF6' }
];

const createCustomMarker = (category, name) => {
    const logoUrl = CATEGORY_LOGOS[category] || CATEGORY_LOGOS.kantor_polisi;
    const cat = CATEGORIES.find(c => c.id === category) || CATEGORIES[0];
    const color = cat.color;
    
    return L.divIcon({
        className: 'custom-tactical-marker',
        html: `
            <div class="marker-wrapper">
                <div class="marker-logo-container">
                    <img src="${logoUrl}" class="marker-logo" />
                </div>
                <div class="marker-pin" style="background: ${color}; box-shadow: 0 0 10px ${color};"></div>
                <div class="marker-label">${name}</div>
            </div>
        `,
        iconSize: [100, 100],
        iconAnchor: [50, 65], // Titik pin ada di anchor
        popupAnchor: [0, -65]
    });
};

// Icon User (Pulse) agar tidak blank
const userIcon = L.divIcon({
    className: 'user-location-marker',
    html: '<div class="pulse-ring"></div><div class="pulse-dot"></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
});

function RecenterAutomatically({ pos }) {
    const map = useMap();
    useEffect(() => { 
        if (pos) map.flyTo([pos.lat, pos.lng], 15, { animate: true, duration: 1.5 }); 
    }, [pos, map]);
    return null;
}

const App = () => {
    const [locations, setLocations] = useState([]);
    const [userPos, setUserPos] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState(['kantor_polisi']);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false); // Default tutup biar ringan pas load awal
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(() => {
        return localStorage.getItem('pwa-installed') === 'true' || 
               window.matchMedia('(display-mode: standalone)').matches || 
               window.navigator.standalone === true;
    });

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setIsInstalled(false);
            localStorage.setItem('pwa-installed', 'false');
        };

        const handleAppInstalled = () => {
            setIsInstalled(true);
            localStorage.setItem('pwa-installed', 'true');
            setDeferredPrompt(null);
            console.log('PWA was installed');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        // Tambahan: cek ulang secara berkala atau saat pindah tab jika diperlukan
        const checkInstallState = () => {
            if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
                setIsInstalled(true);
                localStorage.setItem('pwa-installed', 'true');
            }
        };
        checkInstallState();

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const handleInstallClick = async () => {
        if (isInstalled || localStorage.getItem('pwa-installed') === 'true') {
            alert("Aplikasi sudah terinstal di desktop Anda. Anda akan diarahkan ke versi web.");
            window.location.href = window.location.origin;
            return;
        }

        if (!deferredPrompt) {
            alert("Fitur instalasi belum tersedia atau aplikasi mungkin sudah terinstal. Gunakan menu Browser untuk menginstal jika tombol tidak berfungsi.");
            return;
        }

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setDeferredPrompt(null);
            setIsInstalled(true);
            localStorage.setItem('pwa-installed', 'true');
        }
    };

    // --- OPTIMASI 2: HITUNG JARAK (Dibuat konstan) ---
    const getDistance = useCallback((lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * (Math.PI / 180);
        const dLon = (lon2 - lon1) * (Math.PI / 180);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                  Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return (R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)))).toFixed(2);
    }, []);

    const fetchLocations = useCallback(async (currentPos = userPos) => {
        setLoading(true);
        try {
            // Kita ambil perkategori dan gabungkan
            const requests = selectedCategories.map(cat => 
                axios.get(`https://yanmaps.polisihebat.org/api/locations?category=${cat}`)
            );
            const responses = await Promise.all(requests);
            let allData = responses.flatMap(res => res.data.locations);

            if (currentPos) {
                allData = allData.map(l => ({ 
                    ...l, 
                    distance: parseFloat(getDistance(currentPos.lat, currentPos.lng, l.lat, l.lng)) 
                })).sort((a, b) => a.distance - b.distance);
            }
            setLocations(allData);
        } catch (e) { console.error("Sync Error"); }
        finally { setLoading(false); }
    }, [selectedCategories, userPos, getDistance]);

    useEffect(() => { fetchLocations(); }, [fetchLocations]);

    const handleFindMe = () => {
        if (!navigator.geolocation) return alert("GPS tidak didukung");
        navigator.geolocation.getCurrentPosition((p) => {
            const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
            setUserPos(pos);
            fetchLocations(pos);
        }, () => alert("Aktifkan GPS!"));
    };

    // --- OPTIMASI 3: MEMOIZE MARKERS (Hanya render marker jika lokasi berubah) ---
    const renderedMarkers = useMemo(() => (
        locations.map((l) => (
            <Marker key={l.id} position={[l.lat, l.lng]} icon={createCustomMarker(l.category, l.name)}>
                <Popup className="dark-popup">
                    <div className="p-card">
                        <strong>{l.name}</strong>
                        <p className="addr-text">{l.address}</p>
                        <div className="dist-text">📏 {l.distance ? `${l.distance} KM` : '-- KM'}</div>
                        <div className="btns-row">
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${l.lat},${l.lng}`} target="_blank" rel="noreferrer" className="btn-rute-fix">🚀 RUTE</a>
                            {l.phone && <a href={`tel:${l.phone}`} className="btn-telp-fix">📞 TELP</a>}
                        </div>
                    </div>
                </Popup>
            </Marker>
        ))
    ), [locations]);

    return (
        <div className="yanmaps-container tactical-dark">
            <div className="header dark-panel">
                <div className="header-top">
                    <img src="/image/logo_polisi.png" alt="Polri" className="logo-tribrata" />
                    <div className="titles">
                        <h2>Yanmaps Semarang</h2>
                        <p>Command Center Polrestabes</p>
                    </div>
                </div>
                <div className="category-checklist">
                    {CATEGORIES.map(cat => (
                        <label key={cat.id} className={`checklist-item ${selectedCategories.includes(cat.id) ? 'active' : ''}`}>
                            <input 
                                type="checkbox" 
                                checked={selectedCategories.includes(cat.id)} 
                                onChange={() => {
                                    if (selectedCategories.includes(cat.id)) {
                                        if (selectedCategories.length > 1) {
                                            setSelectedCategories(selectedCategories.filter(c => c !== cat.id));
                                        }
                                    } else {
                                        setSelectedCategories([...selectedCategories, cat.id]);
                                    }
                                }} 
                                hidden
                            />
                            <span>{cat.emoji} {cat.label}</span>
                        </label>
                    ))}
                    <button onClick={handleFindMe} className="gps-btn-header">📍 LOKASI</button>
                </div>
            </div>

            <div className="map-view">
                <MapContainer center={[-6.9912, 110.4085]} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false} preferCanvas={true}>
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                    {userPos && (
                        <>
                            <Marker position={[userPos.lat, userPos.lng]} icon={userIcon}><Popup>Anda di sini</Popup></Marker>
                            <RecenterAutomatically pos={userPos} />
                        </>
                    )}
                    {renderedMarkers}
                </MapContainer>
            </div>

            <div className={`drawer ${drawerOpen ? 'open' : 'closed'}`}>
                <div className="handle" onClick={() => setDrawerOpen(!drawerOpen)}><div className="bar"></div></div>
                <div className="drawer-content">
                    <h3 className="drawer-h3">📍 Lokasi Terdekat</h3>
                    {loading ? <p className="loading-txt">Loading...</p> : locations.slice(0, 15).map((l, i) => (
                        <div key={l.id} className="item-row">
                            <div className="item-info">
                                <div className="name-bold">{i+1}. {l.name}</div>
                                <div className="addr-sub">{l.address}</div>
                            </div>
                            <div className="badge-dist">{l.distance || '--'} KM</div>
                        </div>
                    ))}
                </div>
            </div>

            { (deferredPrompt || isInstalled || localStorage.getItem('pwa-installed') === 'true') && (
                <button 
                    className={`install-pwa-btn ${ (isInstalled || localStorage.getItem('pwa-installed') === 'true') ? 'installed' : ''}`} 
                    onClick={handleInstallClick} 
                    title={ (isInstalled || localStorage.getItem('pwa-installed') === 'true') ? "Sudah Diinstal" : "Instal ke Desktop"}
                >
                    <img src="/image/logo_polisi.png" alt="Install" />
                    <span>{ (isInstalled || localStorage.getItem('pwa-installed') === 'true') ? 'Sudah Diinstal' : 'Instal Aplikasi'}</span>
                </button>
            )}
        </div>
    );
};

export default App;